import React, { useState } from "react";
import { MapPin, Copy, Check, ExternalLink } from "lucide-react";

/**
 * Build a search query combining the address and city for maps providers.
 */
const buildQuery = (address, city) => {
  const parts = [address, city].filter((s) => s && String(s).trim());
  return parts.join(", ");
};

/**
 * "Itinéraire" button — opens Google Maps directions in a new tab.
 *
 * Props:
 *  - address, city: destination components (at least one is required to show the button)
 *  - size: "sm" | "md" (default "sm")
 */
export const DirectionsButton = ({ address, city, size = "sm" }) => {
  const query = buildQuery(address, city);
  if (!query) return null;

  const gmapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;

  const padding = size === "md" ? "px-4 py-2" : "px-3 py-2";
  const textSize = size === "md" ? "text-sm" : "text-xs";

  return (
    <a
      href={gmapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`${padding} ${textSize} inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg font-medium transition-colors`}
      data-testid="directions-btn"
      title="Ouvrir l'itinéraire dans Google Maps"
    >
      <MapPin className="w-4 h-4" />
      Itinéraire
      <ExternalLink className="w-3 h-3 opacity-60" />
    </a>
  );
};

/**
 * "Copier l'adresse" button — copies address to clipboard with feedback.
 */
export const CopyAddressButton = ({ address, city, size = "sm" }) => {
  const [copied, setCopied] = useState(false);
  const full = buildQuery(address, city);
  if (!full) return null;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silent failure (can happen on http or in insecure contexts)
    }
  };

  const padding = size === "md" ? "px-4 py-2" : "px-3 py-2";
  const textSize = size === "md" ? "text-sm" : "text-xs";

  return (
    <button
      onClick={onCopy}
      className={`${padding} ${textSize} inline-flex items-center gap-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors`}
      data-testid="copy-address-btn"
      title="Copier l'adresse"
    >
      {copied ? (
        <>
          <Check className="w-4 h-4 text-emerald-600" />
          <span className="text-emerald-600">Copié !</span>
        </>
      ) : (
        <>
          <Copy className="w-4 h-4" />
          Copier l'adresse
        </>
      )}
    </button>
  );
};
